import {
  Button,
  EmptyState,
  LoadingState,
  PaginationControls,
} from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { useDebounce } from '@hooks/useDebounce';
import { useClientsPaginated, useUpdateClient } from '@hooks/useClients';
import React, { useMemo, useState } from 'react';
import { selectCurrentUser } from '../../../store/auth/authSelectors';
import type { SalesClient } from '../../../types/order';
import { ClientEditorModal } from './ClientEditorModal';
import { ClientsSearchBar } from './ClientsSearchBar';
import { ClientsStatsCards } from './ClientsStatsCards';
import { ClientsTable } from './ClientsTable';
import { MissingCompanyContext } from './MissingCompanyContext';

const CLIENTS_PER_PAGE = 20;

export const ClientsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(1);
  const [editingClient, setEditingClient] = useState<SalesClient | null>(null);

  const hasCompany = Boolean(currentUser?.company?.id);

  const { data, isLoading, isError, error, refetch } = useClientsPaginated({
    search: debouncedSearch,
    page,
    pageSize: CLIENTS_PER_PAGE,
  });

  const updateClient = useUpdateClient();

  const pagination = data?.pagination;

  const clients = useMemo(() => data?.data ?? [], [data]);

  const stats = useMemo(
    () => ({
      withEmail: clients.filter((client) => Boolean(client.email)).length,
      withContact: clients.filter((client) => Boolean(client.contact)).length,
      withTin: clients.filter((client) => Boolean(client.tin_number)).length,
    }),
    [clients],
  );

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  if (!hasCompany) {
    return <MissingCompanyContext />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-black">Clients</h2>
          <p className="mt-1 text-sm text-gray-600">
            Company client directory shared across all outlets.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </div>

      {isError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Failed to load clients.'}
        </div>
      ) : null}

      <ClientsStatsCards
        totalClients={pagination?.totalItems ?? 0}
        withEmail={stats.withEmail}
        withContact={stats.withContact}
        withTin={stats.withTin}
      />

      <ClientsSearchBar value={searchQuery} onChange={handleSearchChange} />

      {isLoading && clients.length === 0 ? (
        <LoadingState message="Loading company clients..." size="lg" />
      ) : clients.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-10">
          {searchQuery ? (
            <EmptyState
              title="No clients found"
              description="Try a different search term to find the client you are looking for."
            />
          ) : (
            <EmptyState
              title="No clients available"
              description="Client records will appear here after they are added through sales or client management flows."
            />
          )}
        </div>
      ) : (
        <>
          <ClientsTable clients={clients} onEdit={setEditingClient} />
          {pagination && (
            <PaginationControls
              pagination={pagination}
              itemLabel={pagination.totalItems !== 1 ? 'clients' : 'client'}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {editingClient && (
        <ClientEditorModal
          client={editingClient}
          isSaving={updateClient.isPending}
          onClose={() => setEditingClient(null)}
          onSubmit={(form) => {
            updateClient.mutate(form, {
              onSuccess: () => setEditingClient(null),
            });
          }}
        />
      )}
    </div>
  );
};
